import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build';
import SchoolIcon from '@mui/icons-material/School';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { Link } from 'react-router-dom';

const CmmcConsultancy: React.FC = () => {
  const consultancyServices = [
    {
      title: "Gap Analysis & Readiness Assessment",
      description: "We assess your current cybersecurity posture against CMMC requirements to identify gaps and develop a prioritized remediation roadmap.",
      icon: <AssessmentIcon fontSize="large" />,
      benefits: [
        "Understand your organization's current CUI protection maturity",
        "Identify gaps across all 17 CMMC domains",
        "Prioritize remediation efforts based on risk",
        "Establish realistic timelines for certification"
      ]
    },
    {
      title: "Control Implementation",
      description: "We help you implement and document the security controls required for your target CMMC level, aligned with NIST SP 800-171.",
      icon: <SettingsIcon fontSize="large" />,
      benefits: [
        "Structured implementation of access control, audit, and other domains",
        "Policies, procedures, and technical controls",
        "Reduced risk of findings during formal assessment",
        "Sustainable, defensible security posture"
      ]
    },
    {
      title: "CMMC Training & Awareness",
      description: "We deliver training on CMMC requirements, CUI handling, and implementation best practices for your entire organization.",
      icon: <SchoolIcon fontSize="large" />,
      benefits: [
        "Build internal CMMC and CUI protection capability",
        "Prepare staff for assessor interviews and evidence requests",
        "Consistent understanding of CMMC practices across teams",
        "Ongoing awareness to maintain certification"
      ]
    },
    {
      title: "Pre-Assessment Preparation",
      description: "We prepare you for your formal CMMC assessment, including mock assessments, evidence organization, and strategic guidance.",
      icon: <BuildIcon fontSize="large" />,
      benefits: [
        "Pre-assessment reviews to surface potential issues",
        "Mock assessments to simulate the real experience",
        "Evidence collection and documentation guidance",
        "Strategic advice to optimize assessment outcomes"
      ]
    }
  ];

  const faqs = [
    {
      question: "What is CMMC and why do we need it?",
      answer: "The Cybersecurity Maturity Model Certification (CMMC) is a DoD framework that ensures defense contractors and subcontractors adequately protect Controlled Unclassified Information (CUI). CMMC is required for many DoD contracts. Our consultancy helps you understand the model, implement the required controls, and prepare for certification."
    },
    {
      question: "How long does CMMC implementation typically take?",
      answer: "Implementation timelines depend on your current security posture, target CMMC level (1, 2, or 3), and organizational size. Level 1 often takes 3–6 months; Levels 2 and 3 can take 6–18 months or more. Our gap analysis provides a tailored timeline based on your specific situation."
    },
    {
      question: "Can you help us prepare for a specific CMMC level?",
      answer: "Yes. We support preparation for CMMC Level 1 (Foundational), Level 2 (Advanced), and Level 3 (Expert). We align your controls with NIST SP 800-171 and CMMC assessment objectives, and help you build the evidence and documentation needed for a successful third-party assessment."
    },
    {
      question: "What resources do we need to commit to CMMC implementation?",
      answer: "Successful implementation requires executive support, dedicated project ownership, IT and security involvement, and investment in tools and processes. We help you define roles, responsibilities, and resource allocation so you can achieve and sustain certification efficiently."
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              CMMC Consultancy Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Expert guidance to achieve CMMC certification and protect Controlled Unclassified Information (CUI)
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Introduction Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6,
          alignItems: 'center' 
        }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Our CMMC Consultancy Approach
              </Typography>
              <Typography paragraph>
                At Right Track Technologies Limited, our CMMC consultancy services help defense contractors and
                organizations implement the cybersecurity practices required to protect CUI and achieve CMMC
                certification. We provide end-to-end support from gap analysis through pre-assessment readiness.
              </Typography>
              <Typography paragraph>
                We align our work with the CMMC model, NIST SP 800-171, and DoD requirements. Whether you are
                targeting Level 1, 2, or 3, we design a tailored implementation plan that fits your environment,
                resources, and contract timelines.
              </Typography>
              <Typography paragraph>
                Our goal is not only to help you pass an assessment but to build a sustainable, defensible
                cybersecurity program that protects CUI and supports your long-term compliance needs.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="CMMC Consultancy - Cybersecurity"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Services Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Our Consultancy Services
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              Our CMMC consultancy services help you implement required controls, close gaps, and prepare
              for a successful third-party CMMC assessment.
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(4, 1fr)' 
            },
            gap: 3 
          }}>
            {consultancyServices.map((service, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            backgroundColor: '#1976d215',
                            color: '#1976d2',
                            mr: 3
                          }}
                        >
                          {service.icon}
                        </Box>
                        <Typography
                          variant="h5"
                          component="h3"
                          fontWeight="bold"
                        >
                          {service.title}
                        </Typography>
                      </Box>
                      
                      <Typography paragraph color="text.secondary" sx={{ mb: 3 }}>
                        {service.description}
                      </Typography>
                      
                      <Divider sx={{ my: 3 }} />
                      
                      <Typography variant="h6" gutterBottom fontWeight="medium">
                        Benefits:
                      </Typography>
                      
                      <List>
                        {service.benefits.map((benefit, idx) => (
                          <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleOutlineIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={benefit} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6,
          alignItems: 'center' 
        }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="CMMC Consultancy Team"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Why Choose Our CMMC Consultancy?
              </Typography>
              <Typography paragraph sx={{ mb: 4 }}>
                Right Track Technologies Limited delivers CMMC consultancy focused on practical implementation,
                CUI protection, and certification readiness. We combine cybersecurity expertise with experience
                in defense and federal contracting environments.
              </Typography>
              
              <List>
                {[
                  "Deep knowledge of CMMC, NIST SP 800-171, and DoD requirements",
                  "Proven approach to gap analysis and control implementation",
                  "Business-focused, risk-based implementation plans",
                  "Customized support for your target CMMC level and scope",
                  "End-to-end support from readiness through pre-assessment",
                  "Training and capability building for your team",
                  "Ongoing guidance for sustainment and recertification"
                ].map((item, index) => (
                  <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* FAQs Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 2 }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              Common questions about our CMMC consultancy services.
            </Typography>
          </motion.div>
          
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Accordion
                  elevation={0}
                  sx={{
                    mb: 2,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary" />}
                    sx={{ px: 3, py: 1.5 }}
                  >
                    <Typography fontWeight="medium">{faq.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, py: 2, bgcolor: 'rgba(245, 247, 250, 0.5)' }}>
                    <Typography color="text.secondary">{faq.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                position: 'relative'
              }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ready to Achieve CMMC Certification?
              </Typography>
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ 
                  maxWidth: '700px', 
                  mx: 'auto', 
                  mb: 4, 
                  opacity: 0.9 
                }}
              >
                Our CMMC consultants are ready to help you implement required controls and prepare for
                certification. Contact us today for a free consultation.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/contact?service=cmmc-consultancy"
                  sx={{
                    bgcolor: 'white',
                    color: '#0c2340',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/services/cmmc"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px'
                  }}
                >
                  Back to CMMC Services
                </Button>
              </Box>

              {/* Decorative circles */}
              <Box sx={{
                position: 'absolute',
                top: -30,
                left: -30,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
              <Box sx={{
                position: 'absolute',
                bottom: -40,
                right: -40,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default CmmcConsultancy;
